/* eslint-disable react/no-array-index-key */
/* eslint-disable no-alert */
import {
  CardMedia,
  Grid,
  TextField,
  RadioGroup,
  Radio,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Button, Card, CardActions, CardContent } from "@material-ui/core";
import Select from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import _ from "lodash";
import { selectFieldProps, textFieldProps } from "../shared/constants";
import { useCompany } from "../../contexts/CompanyContext";
import AddAlertImages from "./AddAlertImages";
import { useAuth } from "../../contexts/AuthContext";
import SuccessAlert from "./SuccessAlert";
import { useStructure } from "../../contexts/StructureContext";
import alertsService from "../../services/alerts.service";
import BackLink from "../shared/BackLink";
import { supabase } from "../../supabase";

/**
 * Component for creating or editing global alerts.
 *
 * @param {object} props - Component props
 * @param {object} props.dealer - Dealer object if editing individual/group alert
 * @param {Function} props.withSubmit - Function to call after submitting alert
 */
export default function CreateAlert({ dealer, withSubmit = () => {} }) {
  const { currentUser } = useAuth();
  const { downstream } = useStructure();
  const { productTypes, activeSuppliers, leaderLevels } = useCompany();
  // Form state
  const [alert, setAlert] = useState({
    image: null,
    type:
      dealer || (!currentUser.is_admin && currentUser.is_leader)
        ? "individual/group"
        : "segment",
    active: true,
  });

  // Component state
  const [alertId, setAlertId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");

  // Options for selects
  const [formattedProductTypes, setFormattedProductTypes] = useState([]);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [formattedLeaderSubStatuses, setFormattedLeaderSubStatuses] = useState(
    []
  );
  const [userList, setUserList] = useState([]);
  const [users, setUsers] = useState(
    dealer
      ? [
          {
            value: dealer.id,
            label: `Z-${dealer.id}: ${dealer.first_name} ${dealer.surname}`,
          },
        ]
      : []
  );
  const [userSegmentTypes, setUserSegmentTypes] = useState([]);

  const activeOptions = [
    { label: "True", value: true },
    { label: "False", value: false },
  ];

  /**
   * Format product types for select component
   */
  useEffect(() => {
    try {
      const formattedProductTypesArray = [];

      // Use productTypes from context instead of activeProductTypes
      if (
        productTypes &&
        Array.isArray(productTypes) &&
        productTypes.length > 0
      ) {
        productTypes.forEach(type => {
          if (type && type.id && type.name) {
            formattedProductTypesArray.push({
              label: type.name,
              value: type.id,
            });
          }
        });
      }

      setFormattedProductTypes(formattedProductTypesArray);
    } catch (error) {
      console.error("Error formatting product types:", error);
    }
  }, [productTypes]);

  /**
   * Format downstream users for select component
   */
  useEffect(() => {
    try {
      const userOptionsArray = [];

      if (downstream && Array.isArray(downstream)) {
        downstream.forEach(userInstance => {
          if (userInstance && userInstance.id) {
            userOptionsArray.push({
              value: userInstance.id,
              label: `Z-${userInstance.id}: ${userInstance.first_name || ""} ${
                userInstance.surname || ""
              }`,
            });
          }
        });
      }

      setUserList(userOptionsArray);
    } catch (error) {
      console.error("Error formatting user list:", error);
    }
  }, [downstream]);

  /**
   * Format leader sub statuses for select component
   */
  useEffect(() => {
    try {
      const formattedLeaderSubStatusesArray = [];

      // Using leaderLevels instead of leaderSubStatuses based on CompanyContext
      if (
        leaderLevels &&
        Array.isArray(leaderLevels) &&
        leaderLevels.length > 0
      ) {
        leaderLevels.forEach(type => {
          if (type) {
            // Adjust property names based on the actual structure from CompanyContext
            const levelName = type.level_name || type.name;
            const levelValue = type.level || type.id;

            if (levelName !== undefined && levelValue !== undefined) {
              formattedLeaderSubStatusesArray.push({
                label: levelName,
                value: levelValue,
              });
            }
          }
        });
      }

      // Only sort if we have items in the array
      if (formattedLeaderSubStatusesArray.length > 0) {
        setFormattedLeaderSubStatuses(
          _.sortBy(formattedLeaderSubStatusesArray, "value")
        );
      } else {
        setFormattedLeaderSubStatuses([]);
      }
    } catch (error) {
      console.error("Error formatting leader sub statuses:", error);
    }
  }, [leaderLevels]);

  /**
   * Format suppliers for select component
   */
  useEffect(() => {
    try {
      const supplierArray = [];

      if (activeSuppliers && Array.isArray(activeSuppliers)) {
        activeSuppliers.forEach(supplierLocal => {
          // Check for either supplier or supplier_name property (from context)
          const supplierName =
            supplierLocal.supplier || supplierLocal.supplier_name;

          if (supplierLocal && supplierLocal.id && supplierName) {
            supplierArray.push({
              label: supplierName,
              value: supplierLocal.id,
            });
          }
        });
      }

      setAvailableSuppliers(supplierArray);
    } catch (error) {
      console.error("Error formatting suppliers:", error);
    }
  }, [activeSuppliers]);
  useEffect(() => {
    /**
     * Fetches a single alert from the API when the user is editing an alert,
     * and processes the response data into a format usable by the form.
     * If the response data is invalid, it will alert the user and redirect
     * them back to the previous page.
     */
    const fetchAlertData = async () => {
      try {
        if (window.location.href.includes("edit")) {
          setLoading(true);
          setLoadingMessage("Loading alert data...");

          const id = window.location.href.split("/")[4];
          setAlertId(id);

          const response = await alertsService.getSingleAlert({
            user_id: currentUser.id,
            id,
          });

          // Check response structure and extract alert data
          let alertData;
          if (response && response.data) {
            // This handles the case where the API returns { data: { data: {...} } }
            alertData = response.data.data || response.data;
          } else if (response && response.success) {
            // This handles the case where the API returns { success: true, data: {...} }
            alertData = response.data;
          } else {
            // Direct response object
            alertData = response;
          }

          // If we have valid alert data, process it
          if (alertData && alertData.id) {
            // Format product_type for the select if it exists
            if (
              alertData.product_type_names &&
              Array.isArray(alertData.product_type_names)
            ) {
              const formattedProductType = [];
              alertData.product_type_names.forEach((name, index) => {
                if (
                  alertData.product_type_ids &&
                  alertData.product_type_ids[index]
                ) {
                  formattedProductType.push({
                    label: name,
                    value: alertData.product_type_ids[index],
                  });
                }
              });
              alertData.product_type = formattedProductType;
            }

            // Format supplier for the select if it exists
            if (
              alertData.supplier_names &&
              Array.isArray(alertData.supplier_names)
            ) {
              const formattedSupplier = [];
              alertData.supplier_names.forEach((name, index) => {
                if (alertData.supplier_ids && alertData.supplier_ids[index]) {
                  formattedSupplier.push({
                    label: name,
                    value: alertData.supplier_ids[index],
                  });
                }
              });
              alertData.supplier = formattedSupplier;
            }

            setAlert(alertData);
            setEditing(true);
            setLoading(false);
          } else {
            console.error("Invalid alert data format:", response);
            window.alert("Alert not found or invalid data format");
            withSubmit();
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error loading alert data:", error);
        window.alert("Error loading alert data");
        withSubmit();
      }
    };

    fetchAlertData();
  }, [currentUser.id]);

  // fetch user segment types
  useEffect(() => {
    const fetchUserSegments = async () => {
      try {
        const { data, error } = await supabase
          .schema(process.env.REACT_APP_SB_SCHEMA)
          .from("refs_user_segment_type")
          .select("*")
          .order("id", { ascending: true });

        if (error) {
          throw new Error(error);
        }

        setUserSegmentTypes(
          data.map(({ id, type }) => ({ value: id, label: type }))
        );
      } catch (err) {
        console.error(err);
      }
    };

    fetchUserSegments();
  }, []);

  /**
   * Handle changes to input fields
   * @param {string} label - Field name
   * @param {any} value - Field value
   */
  const handleChange = (label, value) => {
    setAlert(prevAlert => ({
      ...prevAlert,
      [label]: value,
    }));
  };

  /**
   * Handle multi-select changes
   * @param {string} label - Field name
   * @param {Array} selectedOptions - Selected options array
   */
  const handleMultiSelect = (label, selectedOptions) => {
    handleChange(label, selectedOptions || []);
  };

  /**
   * Close image dialog
   */
  const handleClose = () => {
    setImageOpen(false);
  };

  /**
   * Delete image from alert
   */
  const handleImageDelete = () => {
    setAlert(prevAlert => ({
      ...prevAlert,
      image: null,
    }));
  };

  /**
   * Create or update a global alert
   * @returns {Promise<{success: boolean, message: string}>} Result of operation
   */
  const createGlobalAlert = async () => {
    try {
      if (editing) {
        try {
          const payload = {
            id: alertId,
            title: alert.title,
            description: alert.description,
            image: alert.image,
            active: alert.active,
          };

          const response = await alertsService.updateAlert(payload);

          if (response.data && response.success) {
            return { success: true, message: "Alert updated successfully." };
          }
          console.error("API error:", response.data);
          return {
            success: false,
            message: response.data?.error || "Failed to update alert",
          };
        } catch (error) {
          console.error("Error updating alert:", error);
          return {
            success: false,
            message: `Error updating alert: ${error.message}`,
          };
        }
      }

      // For creating a new alert, use the API endpoint
      const payload = {
        title: alert.title,
        active: alert.active,
        description: alert.description,
        created_by: currentUser.id,
        type: alert.type,
        image: alert.image,
      };

      // Extract IDs from the selected objects for the API
      if (alert.type === "segment") {
        payload.refs_user_segment_type_id = alert.refs_user_segment_type_id;

        // Add product type IDs if they exist
        if (alert.product_type && alert.product_type.length > 0) {
          payload.product_type_ids = alert.product_type.map(item => item.value);
        }

        // Add supplier IDs if they exist
        if (alert.supplier && alert.supplier.length > 0) {
          payload.supplier_ids = alert.supplier.map(item => item.value);
        }

        // Add supplier IDs if they exist
        if (alert.refs_user_segment_type_id === 3) {
          payload.structure_level = alert.structure_level.map(
            item => item.value
          );
        }
      } else if (alert.type === "individual/group" && users.length > 0) {
        // Add user IDs for individual/group alerts
        payload.target_user_ids = users.map(user => user.value);
      }

      const response = await alertsService.createAlert(payload);

      // Check if response has an id (which indicates success)
      // This handles the case where the API returns {id: '24'} directly
      if (response && response.id) {
        return {
          success: true,
          message: "Alert created successfully.",
          id: response.id,
        };
      }

      // also check the response.data structure
      if (
        response &&
        response.data &&
        (response.data.success || response.data.id)
      ) {
        return {
          success: true,
          message: "Alert created successfully.",
          id: response.data.id,
        };
      }

      return {
        success: false,
        message: response?.error || "Failed to create alert",
      };
    } catch (error) {
      console.error("Error creating/updating alert:", error);
      return {
        success: false,
        message: `An error occurred: ${error.message}`,
      };
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const result = await createGlobalAlert();

      setSubmitting(false);

      if (result.success) {
        window.alert(`Alert ${editing ? "edited" : "created"} successfully`);
        withSubmit();
      } else {
        window.alert(result.message);
      }
    } catch (error) {
      setSubmitting(false);
      console.error("Error submitting form:", error);
      window.alert(`Error: ${error.message}`);
    }
  };

  /**
   * Handle form cancellation
   */
  const handleCancel = () => {
    withSubmit();
  };

  /**
   * Check if form is valid for submission
   */
  const isFormValid = () => {
    if (submitting) return false;
    if (!alert.title) return false;
    if (alert.active === undefined) return false;

    if (!editing) {
      if (alert.type === "segment") {
        return (
          alert.refs_user_segment_type_id !== undefined &&
          (alert.refs_user_segment_type_id !== 3 ||
            alert.structure_level?.length > 0)
        );
      }

      if (alert.type === "individual/group") {
        return users.length > 0;
      }
    }

    return true;
  };

  /**
   * Render segment-specific form fields
   */
  const renderSegmentFields = () => {
    if (editing) {
      return (
        <>
          <Typography variant="h6" className="mb-2">
            Suppliers:
          </Typography>
          <div
            style={{ marginBottom: 20, marginTop: 10 }}
            className="space-y-1"
          >
            {alert.supplier_names &&
              alert.supplier_names.map((supplier, index) => (
                <Typography key={`supplier-${index}`}>{supplier}</Typography>
              ))}
          </div>
          <Typography variant="h6" className="mb-2">
            Product Types:
          </Typography>
          <div
            style={{ marginBottom: 20, marginTop: 10 }}
            className="mt-4 space-y-1"
          >
            {alert.product_type_names &&
              alert.product_type_names.map((productType, index) => (
                <Typography key={`productType-${index}`}>
                  {productType}
                </Typography>
              ))}
          </div>
        </>
      );
    }

    return (
      <>
        <div style={{ marginTop: 20 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">User Segment</FormLabel>
            <RadioGroup
              aria-label="refs_user_segment_type_id"
              name="refs_user_segment_type_id"
              value={alert.refs_user_segment_type_id || ""}
              onChange={e => {
                setAlert(prevState => ({
                  ...prevState,
                  refs_user_segment_type_id: parseInt(e.target.value),
                  structure_level: [],
                }));
              }}
            >
              {userSegmentTypes.map(type => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={<Radio />}
                  label={type.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        {alert.refs_user_segment_type_id &&
          alert.refs_user_segment_type_id === 3 && (
            <div style={{ marginTop: 20 }}>
              <Typography variant="subtitle1">Structure Levels</Typography>
              <Select
                label="Structure Levels"
                required
                isMulti
                value={alert.structure_level || []}
                options={formattedLeaderSubStatuses}
                onChange={e => handleMultiSelect("structure_level", e)}
                {...selectFieldProps}
              />
            </div>
          )}

        <div style={{ marginTop: 20 }}>
          <Typography variant="subtitle1">Product Type</Typography>
          <Select
            label="Product Type"
            required
            isMulti
            value={alert.product_type || []}
            options={formattedProductTypes}
            onChange={e => handleMultiSelect("product_type", e)}
            {...selectFieldProps}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Typography variant="subtitle1">Suppliers</Typography>
          <Select
            label="Suppliers"
            required
            isMulti
            options={availableSuppliers}
            value={alert.supplier || []}
            onChange={e => handleMultiSelect("supplier", e)}
            {...selectFieldProps}
          />
        </div>
      </>
    );
  };

  /**
   * Render individual/group-specific form fields
   */
  const renderIndividualGroupFields = () => {
    if (editing) {
      return (
        <div>
          <Typography variant="h6" className="mb-2">
            Users:
          </Typography>
          <div
            style={{ marginBottom: 20, marginTop: 10 }}
            className="mt-4 space-y-1"
          >
            {alert.user_names &&
              alert.user_names.map((user, index) => (
                <Typography key={`user-${index}`}>{user}</Typography>
              ))}
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginTop: 20 }}>
        <Typography variant="subtitle1">Select Users</Typography>
        <Select
          isMulti
          disabled={userList.length === 0}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
          }}
          placeholder="Select Users To Send Alert To"
          options={userList}
          onChange={val => {
            setUsers(val || []);
          }}
          value={users}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: 16 }}>
          {loadingMessage}
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {dealer === undefined || dealer === null ? <BackLink /> : null}
      <SuccessAlert
        open={success}
        setOpen={setSuccess}
        text="Alert Successfully Created"
      />
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {editing ? "Edit Alert" : "Create Alert"}
          </Typography>

          <form>
            {/* Alert Title */}
            <TextField
              required
              name="title"
              label="Alert Name"
              value={alert.title || ""}
              {...textFieldProps}
              onChange={e => handleChange(e.target.name, e.target.value)}
            />

            {/* Active Status */}
            <Typography variant="subtitle1" style={{ marginTop: 16 }}>
              Active
            </Typography>
            <Select
              {...selectFieldProps}
              required
              value={
                alert.active !== undefined
                  ? {
                      label: alert.active ? "True" : "False",
                      value: alert.active,
                    }
                  : null
              }
              options={activeOptions}
              onChange={e => handleChange("active", e.value)}
            />

            {/* Alert Description */}
            <Typography variant="subtitle1" style={{ marginTop: 16 }}>
              Description
            </Typography>
            <CKEditor
              required
              editor={ClassicEditor}
              data={alert.description || ""}
              onChange={(e, editor) => {
                const data = editor.getData();
                handleChange("description", data);
              }}
            />

            {/* Alert Image */}
            {alert.image && (
              <Card elevation={3} style={{ margin: "16px 0" }}>
                <Grid container>
                  <Grid item md={6} xs={12}>
                    <CardMedia
                      component="img"
                      image={alert.image}
                      alt="Alert image"
                      style={{
                        margin: 5,
                        maxHeight: 300,
                        maxWidth: 300,
                      }}
                    />
                    <CardContent>
                      <Button
                        style={{ marginLeft: 10 }}
                        variant="outlined"
                        color="secondary"
                        onClick={handleImageDelete}
                      >
                        Delete
                      </Button>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            )}

            {/* Add Image Button */}
            <Button
              onClick={() => setImageOpen(true)}
              variant="contained"
              color="primary"
              style={{ marginTop: 16 }}
            >
              Add Image
            </Button>

            {/* Alert Type Selection (only for new alerts) */}
            {!editing && currentUser.is_admin && (
              <div style={{ marginTop: 20 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Type</FormLabel>
                  <RadioGroup
                    aria-label="type"
                    name="type"
                    value={alert.type}
                    onChange={e => {
                      handleChange("type", e.target.value);
                    }}
                  >
                    <FormControlLabel
                      value="segment"
                      control={<Radio />}
                      label="Segment"
                    />
                    <FormControlLabel
                      value="individual/group"
                      control={<Radio />}
                      label="Individual/Group"
                    />
                  </RadioGroup>
                </FormControl>
              </div>
            )}

            {/* Show alert type in edit mode */}
            {editing && (
              <div style={{ marginTop: 20 }}>
                <Typography variant="h6" className="mb-2">
                  Alert Type:
                </Typography>
                <div style={{ marginBottom: 20, marginTop: 10 }}>
                  <Typography style={{ textTransform: "capitalize" }}>
                    {alert.type}
                  </Typography>
                </div>
              </div>
            )}

            {/* Type-specific fields */}
            {alert.type === "segment" && currentUser.is_admin
              ? renderSegmentFields()
              : renderIndividualGroupFields()}

            {/* Debug info to help troubleshoot - remove in production */}
            {process.env.NODE_ENV !== "production" && (
              <div
                style={{
                  marginTop: 20,
                  borderTop: "1px solid #eee",
                  paddingTop: 10,
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  Debug Information:
                </Typography>
                <Typography variant="caption" display="block">
                  Product Types Available: {formattedProductTypes.length}
                </Typography>
                <Typography variant="caption" display="block">
                  Suppliers Available: {availableSuppliers.length}
                </Typography>
                <Typography variant="caption" display="block">
                  Leader Levels Available: {formattedLeaderSubStatuses.length}
                </Typography>
              </div>
            )}
          </form>
        </CardContent>

        <CardActions>
          <Button
            disabled={!isFormValid()}
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {submitting ? "Loading..." : "Submit"}
          </Button>
          <Button
            disabled={submitting}
            onClick={handleCancel}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
        </CardActions>
      </Card>
      {/* Image Upload Dialog */}
      <AddAlertImages
        open={imageOpen}
        close={handleClose}
        alert={alert}
        setAlert={setAlert}
      />
    </div>
  );
}
